// Configuração resolvida em runtime, a partir do `window.__ENV__` que o
// entrypoint.sh do container escreve em `env.js` na subida.
//
// Não há default de ambiente aqui de propósito. A versão anterior caía em
// URLs de homologação quando a variável faltava — e como o hostname de
// produção não casava com nenhuma regra, um `env.js` ausente ou malformado
// fazia a aplicação apontar silenciosamente para a base errada, sem erro
// visível. Agora falta de configuração falha alto: console.error e valor
// vazio, que quebra a chamada em vez de mandá-la para o outro ambiente.
//
// A única exceção é o desenvolvimento local, onde não existe container para
// gerar o `env.js`. Ali a URL da API vem do `REACT_APP_API_URL` que o
// scripts/configurar-api-dev.js escolhe a partir do `.env.local` (ver
// getApiBaseUrl).

const LOCAL_HOSTS = ['localhost', '127.0.0.1', '[::1]'];

const LOCAL_DEFAULTS = {
  EGRC_API_URL_URL: 'https://localhost:7176/api/v1/',
  EGRC_COLLABORA_URL: 'http://localhost:9980',
  LO_BASE_URL: 'http://localhost:9980'
};

// Configuração sem a qual a aplicação não funciona. Ausência é erro, não
// motivo para adivinhar um valor.
const REQUIRED_KEYS = ['EGRC_API_URL_URL', 'EGRC_COLLABORA_URL'];

// Opcionais: ausência é legítima e o valor vazio já é o comportamento certo.
const OPTIONAL_DEFAULTS = {
  MAPBOX_ACCESS_TOKEN: '',
  BASE_NAME: '',
  GOOGLE_MAPS_API_KEY: ''
};

const reportedKeys = new Set();

function isLocalHost() {
  if (typeof window === 'undefined' || !window.location) return false;
  return LOCAL_HOSTS.includes(window.location.hostname);
}

function readWindowEnv() {
  if (typeof window === 'undefined') return {};
  return window.__ENV__ || {};
}

function reportMissing(key) {
  if (reportedKeys.has(key)) return;
  reportedKeys.add(key);

  // eslint-disable-next-line no-console
  console.error(
    `[EGRC] Configuração ausente: ${key}. ` +
      'Defina a variável de ambiente correspondente no container — ela é escrita em env.js na subida.'
  );
}

export function getRuntimeEnv(key) {
  const value = readWindowEnv()[key];

  if (typeof value === 'string' && value.length > 0) return value;

  if (isLocalHost() && LOCAL_DEFAULTS[key] !== undefined) {
    return LOCAL_DEFAULTS[key];
  }

  if (Object.prototype.hasOwnProperty.call(OPTIONAL_DEFAULTS, key)) {
    return OPTIONAL_DEFAULTS[key];
  }

  if (REQUIRED_KEYS.includes(key)) reportMissing(key);

  return '';
}

// Em desenvolvimento (`npm start`) vale o REACT_APP_API_URL escolhido pelo
// scripts/configurar-api-dev.js: assim API_URL e as chamadas que usam
// process.env.REACT_APP_API_URL direto apontam para o mesmo lugar, e o env.js
// versionado não precisa ser editado para trocar de ambiente. No build de
// produção o NODE_ENV é fixo e este ramo some.
function getDevApiBaseUrl() {
  if (process.env.NODE_ENV !== 'development') return '';
  return process.env.REACT_APP_API_URL || '';
}

export function getApiBaseUrl() {
  return getDevApiBaseUrl() || getRuntimeEnv('EGRC_API_URL_URL');
}

export function getCollaboraUrl() {
  return getRuntimeEnv('EGRC_COLLABORA_URL');
}

/**
 * O host WOPI é a própria API unificada — o módulo WOPI deixou de ser um
 * serviço à parte. A URL sai da base da API, sem o sufixo /api/v1.
 */
export function getWopiUrl() {
  const configured = readWindowEnv().EGRC_WOPI_URL;

  if (typeof configured === 'string' && configured.length > 0) return configured;

  return getApiBaseUrl().replace(/\/api\/v1\/?$/, '');
}

export function getMapboxAccessToken() {
  return getRuntimeEnv('MAPBOX_ACCESS_TOKEN');
}

export function getMaxFileSizeMB() {
  const parsed = parseInt(readWindowEnv().MAX_FILE_SIZE_MB, 10);

  if (!isNaN(parsed) && parsed > 0) return parsed;

  return 100;
}

export function getBaseName() {
  const configured = getRuntimeEnv('BASE_NAME');

  if (configured) return configured;

  if (typeof window !== 'undefined') {
    const pathname = window.location.pathname || '';

    if (pathname === '/app' || pathname.startsWith('/app/')) return '/app';
  }

  return '';
}

export function getGoogleMapsApiKey() {
  return getRuntimeEnv('GOOGLE_MAPS_API_KEY');
}

export function getLibreOfficeBaseUrl() {
  return getRuntimeEnv('LO_BASE_URL') || getCollaboraUrl();
}
